import React, { useState, useEffect, useCallback } from "react";
import PageGuide from "../../components/PageGuide";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Container, Stack, Typography, Breadcrumbs, Box, Paper,
  TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination, Button, Chip, CircularProgress, Alert,
  FormControl, InputLabel, Select, MenuItem, Collapse, TextField,
  Tooltip,
} from "@mui/material";
import {
  HomeRounded, ArrowBackRounded, DownloadRounded,
  ExpandMoreRounded, ExpandLessRounded, FilterListRounded,
} from "@mui/icons-material";
import ApiCall from "../../utils/apicall";

import SmartTable from "../../components/SmartTable/SmartTable";
// ─── constants ───────────────────────────────────────────────────────────────

const MODULES = ["heat", "heat_piece_link", "ncr", "job", "vendor", "user", "bol", "fabricated_item", "permission"];
const ACTIONS = ["CREATE", "UPDATE", "DELETE", "LOGIN", "LOGOUT", "VERIFY", "CLOSE", "APPROVE", "REJECT"];

const ACTION_COLOR = {
  CREATE:  "success",
  UPDATE:  "info",
  DELETE:  "error",
  LOGIN:   "default",
  LOGOUT:  "default",
  VERIFY:  "secondary",
  CLOSE:   "warning",
  APPROVE: "success",
  REJECT:  "error",
};

const MODULE_LABEL = {
  heat:             "Heat",
  heat_piece_link:  "Heat–Piece Link",
  ncr:              "NCR",
  job:              "Job",
  vendor:           "Vendor",
  user:             "User",
  bol:              "Bill of Lading",
  fabricated_item:  "Fabricated Item",
  permission:       "Permission",
};

function fmtDateTime(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString();
}

function exportCSV(rows) {
  const header = ["Timestamp", "User", "Action", "Module", "Record ID", "Record Label", "IP Address", "Notes"];
  const data = rows.map((r) => [
    fmtDateTime(r.createdAt),
    r.userName || r.user?.email || "",
    r.action,
    r.module,
    r.recordId   ?? "",
    r.recordLabel ?? "",
    r.ipAddress   ?? "",
    r.notes       ?? "",
  ]);
  const csv = [header, ...data]
    .map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement("a"), { href: url, download: "audit-log.csv" });
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

// ─── component ───────────────────────────────────────────────────────────────

export default function AuditLogReport() {
  const navigate = useNavigate();

  const [filters,      setFilters]      = useState({ module: "", action: "", startDate: "", endDate: "" });
  const [filtersOpen,  setFiltersOpen]  = useState(true);
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(50);
  const [expanded,     setExpanded]     = useState({});

  const buildQuery = useCallback((f, p, rpp) => {
    const params = new URLSearchParams();
    if (f.module)    params.set("module",    f.module);
    if (f.action)    params.set("action",    f.action);
    if (f.startDate) params.set("startDate", f.startDate);
    if (f.endDate)   params.set("endDate",   f.endDate);
    params.set("limit",  String(rpp));
    params.set("offset", String(p * rpp));
    return params.toString();
  }, []);

  const fetchData = useCallback((f, p, rpp) => {
    setLoading(true);
    setError(null);
    ApiCall.get(`/reports/audit-log?${buildQuery(f, p, rpp)}`)
      .then((r) => setData(r.data?.data))
      .catch(() => setError("Failed to load audit log"))
      .finally(() => setLoading(false));
  }, [buildQuery]);

  // Load on mount
  useEffect(() => { fetchData(filters, 0, rowsPerPage); }, []); // eslint-disable-line

  const handleApply = () => {
    setPage(0);
    setExpanded({});
    fetchData(filters, 0, rowsPerPage);
  };

  const handlePageChange = (_, p) => {
    setPage(p);
    setExpanded({});
    fetchData(filters, p, rowsPerPage);
  };

  const handleRppChange = (e) => {
    const rpp = parseInt(e.target.value);
    setRowsPerPage(rpp);
    setPage(0);
    setExpanded({});
    fetchData(filters, 0, rpp);
  };

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const rows = data?.rows || [];

  return (
    <>
      <Helmet><title>Audit Log | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>

        {/* ── header ── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Breadcrumbs>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <HomeRounded fontSize="small" />
              <Typography variant="body2" color="text.secondary">Reports</Typography>
            </Stack>
            <Typography variant="body2" fontWeight={600}>Audit Log</Typography>
          </Breadcrumbs>
          <Stack direction="row" spacing={1}>
            {data && rows.length > 0 && (
              <Button variant="outlined" size="small" startIcon={<DownloadRounded />}
                onClick={() => exportCSV(rows)}>
                Export CSV
              </Button>
            )}
            <Button variant="text" size="small" startIcon={<ArrowBackRounded />}
              onClick={() => navigate("/reports")}>
              All Reports
            </Button>
          </Stack>
        </Stack>
        <PageGuide
          why="Show a complete, tamper-evident trail of who did what and when — the backbone of an audit."
          who="The QM and auditors, reviewing the full activity trail."
          what="The system audit log of recorded actions across the app."
          how="Filter by date, user, or action, then export."
          when="During audits or any time you need to trace an action."
          standard="ISO 9001:2015 §7.5.3"
        />

        {/* ── filter panel ── */}
        <Paper variant="outlined" sx={{ mb: 3 }}>
          <Stack
            direction="row" alignItems="center" justifyContent="space-between"
            px={2} py={1.5} sx={{ cursor: "pointer" }}
            onClick={() => setFiltersOpen((v) => !v)}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <FilterListRounded fontSize="small" color="action" />
              <Typography variant="body2" fontWeight={600}>Filters</Typography>
            </Stack>
            {filtersOpen ? <ExpandLessRounded fontSize="small" /> : <ExpandMoreRounded fontSize="small" />}
          </Stack>
          <Collapse in={filtersOpen}>
            <Box px={2} pb={2}>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Module</InputLabel>
                  <Select value={filters.module} label="Module"
                    onChange={(e) => setFilters((p) => ({ ...p, module: e.target.value }))}>
                    <MenuItem value="">All Modules</MenuItem>
                    {MODULES.map((m) => (
                      <MenuItem key={m} value={m}>{MODULE_LABEL[m] || m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Action</InputLabel>
                  <Select value={filters.action} label="Action"
                    onChange={(e) => setFilters((p) => ({ ...p, action: e.target.value }))}>
                    <MenuItem value="">All Actions</MenuItem>
                    {ACTIONS.map((a) => (
                      <MenuItem key={a} value={a}>{a}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small" label="From Date" type="date"
                  value={filters.startDate}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))}
                />
                <TextField
                  size="small" label="To Date" type="date"
                  value={filters.endDate}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))}
                />
                <Button variant="contained" size="small" onClick={handleApply}>
                  Apply
                </Button>
                <Button variant="text" size="small" onClick={() => {
                  const reset = { module: "", action: "", startDate: "", endDate: "" };
                  setFilters(reset);
                  setPage(0);
                  fetchData(reset, 0, rowsPerPage);
                }}>
                  Reset
                </Button>
              </Stack>
            </Box>
          </Collapse>
        </Paper>

        {/* ── summary tiles ── */}
        {data?.moduleCounts?.length > 0 && (
          <Stack direction="row" spacing={1} mb={3} flexWrap="wrap" useFlexGap>
            {data.moduleCounts.map((mc) => (
              <Chip key={mc.module}
                label={`${MODULE_LABEL[mc.module] || mc.module}: ${mc.count}`}
                size="small" variant="outlined"
                onClick={() => {
                  const f = { ...filters, module: mc.module };
                  setFilters(f);
                  setPage(0);
                  fetchData(f, 0, rowsPerPage);
                }}
              />
            ))}
          </Stack>
        )}

        {/* ── total ── */}
        {data && (
          <Typography variant="body2" color="text.secondary" mb={1}>
            Showing {rows.length} of {data.total} entries
          </Typography>
        )}

        {/* ── states ── */}
        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {loading && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" mt={2}>Loading…</Typography>
          </Box>
        )}
        {!loading && data && rows.length === 0 && (
          <Alert severity="info">No audit entries match the current filters.</Alert>
        )}

        {/* ── table ── */}
        {!loading && rows.length > 0 && (
          <>
            <TableContainer component={Paper} variant="outlined">
              <SmartTable tableId="report-audit-log" lockedColumns={["Timestamp", "User", "Action"]} size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.50" }}>
                    <TableCell width={32} />
                    <TableCell><strong>Timestamp</strong></TableCell>
                    <TableCell><strong>User</strong></TableCell>
                    <TableCell><strong>Action</strong></TableCell>
                    <TableCell><strong>Module</strong></TableCell>
                    <TableCell><strong>Record</strong></TableCell>
                    <TableCell><strong>IP</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <TableRow hover
                        sx={{ "& td": { borderBottom: expanded[row.id] ? "none" : undefined } }}>
                        <TableCell>
                          {(row.previousValues || row.newValues || row.notes) && (
                            <Button size="small" onClick={() => toggleExpand(row.id)}
                              sx={{ minWidth: 0, p: 0.5 }}>
                              {expanded[row.id]
                                ? <ExpandLessRounded fontSize="small" />
                                : <ExpandMoreRounded fontSize="small" />}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap", fontSize: "0.78rem", color: "text.secondary" }}>
                          {fmtDateTime(row.createdAt)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={row.user?.email || ""} arrow>
                            <span>{row.userName || row.user?.email || <em style={{ color: "#999" }}>system</em>}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Chip label={row.action} size="small"
                            color={ACTION_COLOR[row.action] || "default"} />
                        </TableCell>
                        <TableCell>
                          <Chip label={MODULE_LABEL[row.module] || row.module}
                            size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          {row.recordLabel
                            ? <span>{row.recordLabel} <span style={{ color: "#999", fontSize: "0.75rem" }}>#{row.recordId}</span></span>
                            : row.recordId ? `#${row.recordId}` : "—"}
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                          {row.ipAddress || "—"}
                        </TableCell>
                      </TableRow>

                      {/* ── expanded diff row ── */}
                      {(row.previousValues || row.newValues || row.notes) && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                            <Collapse in={!!expanded[row.id]} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 6, py: 2, bgcolor: "grey.50", borderBottom: "1px solid #e0e0e0" }}>
                                <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                                  {row.previousValues && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary"
                                        fontWeight={700} textTransform="uppercase">
                                        Before
                                      </Typography>
                                      <Box component="pre" sx={{
                                        mt: 0.5, fontSize: "0.75rem",
                                        bgcolor: "#fff3e0", p: 1, borderRadius: 1,
                                        maxWidth: 400, overflow: "auto",
                                      }}>
                                        {JSON.stringify(row.previousValues, null, 2)}
                                      </Box>
                                    </Box>
                                  )}
                                  {row.newValues && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary"
                                        fontWeight={700} textTransform="uppercase">
                                        After
                                      </Typography>
                                      <Box component="pre" sx={{
                                        mt: 0.5, fontSize: "0.75rem",
                                        bgcolor: "#e8f5e9", p: 1, borderRadius: 1,
                                        maxWidth: 400, overflow: "auto",
                                      }}>
                                        {JSON.stringify(row.newValues, null, 2)}
                                      </Box>
                                    </Box>
                                  )}
                                  {row.notes && (
                                    <Box>
                                      <Typography variant="caption" color="text.secondary"
                                        fontWeight={700} textTransform="uppercase">
                                        Notes
                                      </Typography>
                                      <Typography variant="body2" mt={0.5}>{row.notes}</Typography>
                                    </Box>
                                  )}
                                </Stack>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </SmartTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={data.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRppChange}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </>
        )}

      </Container>
    </>
  );
}
